/**
 * Smart Editor Module
 *
 * Provides rich text editing capabilities with specialized mapping and content transfer features
 *
 * Provides the following features:
 * - Rich text editing with TinyMCE integration
 * - Source and target document mapping
 * - Content transfer between editors
 * - Custom toolbar and plugin configuration
 * - Font size control
 * - Selection management
 *
 * @module SmartEditor
 */

import {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from 'react';
import { Observer } from 'mobx-react';
import { Editor } from '@tinymce/tinymce-react';
import {
  DOCUMENT_MARGINS,
  DOCUMENT_STYLES,
  MSO_DEFINITIONS,
  REACT_APP_TINYMCE_API_KEY,
} from '../../constants';
import Slider from '../elements/Slider';
import {
  ISmartEditorStore,
  SmartEditorProvider,
  getMenubarByType,
  getPluginsByType,
  getToolbarByType,
  getRemovedMenuitemsByType,
  SourceSmartEditorStore,
  TargetSmartEditorStore,
} from '.';
import { useMappingSession } from '../../contexts';
import { Editor as TinyMCEEditor } from 'tinymce';
import { EditorMode } from '../../stores';
import { MappingContext } from '../../contexts/MappingContext';
import { setupEditorClipboardSupport } from '../../helpers/generalizationHelpers';

/**
 * Editor Type Enumeration
 * Defines available editor modes
 *
 * @enum {string}
 * @property {string} target - Target document editor mode
 * @property {string} source - Source document editor mode
 */
export type SmartEditorType = 'target' | 'source';

/**
 * After Enter, TinyMCE often leaves the caret in a new sibling block without `data-nodeid`.
 * Resolve the owning authoring node by walking preceding siblings / ancestors.
 */
function resolveTargetAuthoringHost(anchor: Node, root: HTMLElement): HTMLElement | null {
  const startEl =
    anchor.nodeType === Node.ELEMENT_NODE ? (anchor as HTMLElement) : anchor.parentElement;
  if (!startEl || !root.contains(startEl)) {
    return null;
  }

  let walk: HTMLElement | null = startEl;
  while (walk && walk !== root) {
    const fromClosest = walk.closest('[data-nodeid]');
    if (fromClosest && root.contains(fromClosest)) {
      return fromClosest as HTMLElement;
    }

    let prev: Element | null = walk.previousElementSibling;
    while (prev) {
      if (prev.hasAttribute('data-nodeid')) {
        return prev as HTMLElement;
      }
      const nestedMarked = prev.querySelector('[data-nodeid]');
      if (nestedMarked) {
        const owner = nestedMarked.closest('[data-nodeid]');
        if (owner && root.contains(owner)) {
          return owner as HTMLElement;
        }
      }
      prev = prev.previousElementSibling;
    }

    walk = walk.parentElement;
  }

  return null;
}

/** Marked block plus following sibling blocks until another `data-nodeid` (split paragraphs). */
function collectLogicalTargetBlockText(markedHost: HTMLElement): string {
  const tag = markedHost.tagName;
  if (tag === 'TD' || tag === 'TH') {
    return markedHost.textContent ?? '';
  }

  const parts: string[] = [];
  let node: Element | null = markedHost;
  while (node) {
    parts.push(node.textContent ?? '');
    const nextEl: Element | null = node.nextElementSibling;
    if (!nextEl || nextEl.hasAttribute('data-nodeid')) {
      break;
    }
    node = nextEl;
  }
  return parts.join('');
}

export interface SmartEditorProps {
  /** Editor type (source/target) */
  type: SmartEditorType;
  /** Initial editor content */
  content?: string;
  /** Editor height (default: '60vh') */
  height?: string;
  /** Custom store instance */
  store?: ISmartEditorStore;
  /** Additional TinyMCE plugins */
  plugins?: string;
  /** Custom toolbar configuration */
  toolbar?: string;
  /** Menu items to remove */
  removedMenuitems?: string;
  /** Function to update dropdown selection */
  updateDropdownSelection?: (sourceId: string) => void;
}

/**
 * Smart Editor Component
 * Rich text editor with mapping and content transfer capabilities
 *
 * @component SmartEditor
 * @param {SmartEditorType} props.type - Editor type (source/target)
 * @param {string} [props.content] - Initial editor content
 * @param {boolean} [props.disabled] - Whether editor is disabled
 * @param {string} [props.height] - Editor height (default: '60vh')
 * @param {ISmartEditorStore} [props.store] - Custom store instance
 * @param {string} [props.plugins] - Additional TinyMCE plugins
 * @param {string} [props.toolbar] - Custom toolbar configuration
 * @param {string} [props.removedMenuitems] - Menu items to remove
 * @param {Function} [props.updateDropdownSelection] - Function to update dropdown selection
 *
 * @example
 * // Basic usage
 * <SmartEditor type="source" content="<p>Initial content</p>" />
 *
 * @example
 * // With custom store and configuration
 * <SmartEditor
 *   type="target"
 *   store={customStore}
 *   plugins="table lists"
 *   toolbar="bold italic"
 * />
 */
export const SmartEditor = memo(
  ({
    type,
    height = '60vh',
    store,
    removedMenuitems,
    plugins,
    toolbar,
    updateDropdownSelection,
  }: SmartEditorProps) => {
    const mappingSessionStore = useMappingSession();
    const editorRef = useRef<TinyMCEEditor | null>(null);
    const { setEditor1Content, editor1Ref, editor2Ref } = useContext(MappingContext);

    /**
     * Initialize smart editor store
     * Creates source or target store instance based on editor type
     *
     * @method initializeStore
     * @param {ISmartEditorStore} [store] - Optional custom store instance
     * @returns {Object} Store reference object
     * @throws {Error} When invalid editor type provided
     *
     * @example
     * const storeRef = initializeStore(customStore);
     */
    const smartEditorStoreRef = useMemo(
      () => ({
        current:
          (store ?? type === 'source')
            ? new SourceSmartEditorStore(mappingSessionStore)
            : new TargetSmartEditorStore(mappingSessionStore, updateDropdownSelection),
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    const smartEditorStore = smartEditorStoreRef.current;

    useEffect(() => {
      return () => {
        setEditor1Content({ html: '' });
        if (type === 'target') {
          (editor1Ref as MutableRefObject<TinyMCEEditor | null>).current = null;
        }
        if (type === 'source') {
          (editor2Ref as MutableRefObject<TinyMCEEditor | null>).current = null;
        }
        if (!store && smartEditorStore) {
          smartEditorStore.dispose();
        }
      };
    }, [store, type, editor1Ref, editor2Ref, setEditor1Content, smartEditorStore]);

    /**
     * Handle font size change in editor
     * Updates zoom level and store font size value
     *
     * @method handleFontSizeChange
     * @param {number} value - New font size percentage value
     * @returns {void}
     *
     * @example
     * handleFontSizeChange(120); // Set zoom to 120%
     */
    const handleFontSizeChange = (value: number) => {
      if (editorRef.current) {
        const body = editorRef.current.getBody();
        (body.style as unknown as { zoom: string }).zoom = `${value}%`;
      }
      smartEditorStore.setFontSize(value);
    };

    /**
     * Configure editor UI elements
     * Sets up menubar, plugins, toolbar and removed items based on editor type
     *
     * @method configureEditor
     * @property {string | boolean} editorMenubar - Menubar configuration for editor type
     * @property {string} editorRemovedMenuitems - Menu items to remove for editor type
     * @property {string} editorPlugins - Plugins enabled for editor type
     * @property {string} editorToolbar - Toolbar configuration for editor type
     *
     * @example
     * // Configuration for source editor
     * const menubar = getMenubarByType('source', removedItems);
     */
    const editorType = mappingSessionStore.editorMode === EditorMode.TRANSFER ? 'source' : type;
    const editorMenubar = getMenubarByType(editorType, removedMenuitems);
    const editorRemovedMenuitems = getRemovedMenuitemsByType(editorType, removedMenuitems);
    const editorPlugins = getPluginsByType(editorType, plugins);
    const editorToolbar = getToolbarByType(editorType, toolbar);

    /**
     * Initialize TinyMCE editor instance
     * Sets up editor content, document, and event handlers
     *
     * @method handleEditorInit
     * @param {unknown} _evt - Editor initialization event
     * @param {TinyMCEEditor} editor - TinyMCE editor instance
     * @returns {Promise<void>}
     * @throws {Error} When editor initialization fails
     *
     * @example
     * // Called internally by TinyMCE
     * await handleEditorInit(event, editorInstance);
     */
    const handleEditorInit = async (_evt: unknown, editor: TinyMCEEditor) => {
      editorRef.current = editor;
      await editor.initialized;

      if (type === 'target') {
        (editor1Ref as MutableRefObject<TinyMCEEditor | null>).current = editor;
      } else {
        (editor2Ref as MutableRefObject<TinyMCEEditor | null>).current = editor;
      }

      // Set initial content and document when editor is ready
      const editorDoc = editor.getDoc();
      smartEditorStore.setEditorDocument(editorDoc);
      if (type === 'target') {
        mappingSessionStore.setBaselineDocument('target', editorDoc);
      }

      // Setup editor event handlers
      setupEditorHandlers(editor);
      setupEditorClipboardSupport(editor, { readOnlyClipboard: type === 'source' });
    };

    const setupEditorHandlers = (editor: TinyMCEEditor) => {
      let pendingMouseDownTarget: Element | null = null;

      editor.on('mousedown', (e) => {
        const target = e.target as Element;
        const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;

        // Defer target cell highlight until mouseup so drag-selecting text does not flash blue.
        if (type === 'target' && !isMultiSelect) {
          pendingMouseDownTarget = target;
          return;
        }

        pendingMouseDownTarget = null;
        handleElementClick(target, isMultiSelect);
      });

      editor.on('mouseup', (e) => {
        const selection = editor.selection;
        const target = e.target as Element;
        const isMultiSelect = e.ctrlKey || e.metaKey || e.shiftKey;

        // In the target editor, Ctrl/Cmd/Shift + click is used for multi-cell selection.
        // TinyMCE can leave a transient non-collapsed text selection; treating it as
        // intentional text selection on mouseup would run handleTextSelection and clear
        // multi-cell highlights (previously only guarded in TRANSFER mode).
        if (type === 'target' && isMultiSelect) {
          if (!selection.isCollapsed()) {
            selection.collapse();
          }
          mappingSessionStore.clearTextSelection();
          pendingMouseDownTarget = null;
          return;
        }

        // Check if this is a text selection
        if (!selection.isCollapsed()) {
          const range = selection.getRng();
          smartEditorStore.handleTextSelection(range, target);
          pendingMouseDownTarget = null;
          return;
        }

        if (pendingMouseDownTarget) {
          handleElementClick(pendingMouseDownTarget, false);
          pendingMouseDownTarget = null;
        }
      });

      // Keyboard (Shift+arrows) and other non-mouse selections never fire mouseup; Link needs
      // stable ranges in the mapping session store.
      editor.on('selectionchange', () => {
        const selection = editor.selection;
        if (selection.isCollapsed()) {
          return;
        }
        const range = selection.getRng();
        const anchor = selection.getNode() as Element;
        smartEditorStore.handleTextSelection(range, anchor);
      });

      editor.on('dblclick', (e) => {
        const target = e.target as Element;
        handleElementDoubleClick(target);
      });

      let targetEditSyncTimer: number | undefined;
      const syncTargetInlineEdits = () => {
        if (type !== 'target' || mappingSessionStore.editorMode !== EditorMode.EDITING) {
          return;
        }
        const baseline = mappingSessionStore.getBaselineDocument('target');
        if (!baseline) {
          return;
        }

        const anchor = editor.selection.getNode() as Node;
        const host = resolveTargetAuthoringHost(anchor, editor.getBody());
        if (!host) {
          return;
        }

        const nodeId = host.getAttribute('data-nodeid');
        if (!nodeId || mappingSessionStore.isLocked(nodeId)) {
          return;
        }

        const esc =
          typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
            ? CSS.escape(nodeId)
            : nodeId.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const baselineNode = baseline.querySelector(`[data-nodeid="${esc}"]`);
        const curText = collectLogicalTargetBlockText(host);
        const baseText = baselineNode
          ? collectLogicalTargetBlockText(baselineNode as HTMLElement)
          : '';
        if (curText !== baseText) {
          mappingSessionStore.recordNodeChange('target', nodeId, baseText, curText);
        } else {
          mappingSessionStore.clearTrackedNodeChange('target', nodeId);
        }
        if (smartEditorStore instanceof TargetSmartEditorStore) {
          smartEditorStore.refreshAuthoringHighlightForNode(nodeId);
        }
      };

      if (type === 'target') {
        editor.on('change', () => {
          window.clearTimeout(targetEditSyncTimer);
          targetEditSyncTimer = window.setTimeout(syncTargetInlineEdits, 200);
        });
      }
    };

    const handleElementDoubleClick = (element: Element) => {
      // Only handle double-clicks in target editor during transfer mode
      if (type !== 'target' || mappingSessionStore.editorMode !== EditorMode.TRANSFER) {
        return;
      }

      const editor = editorRef.current;
      if (!editor) {
        return;
      }

      // Clear any text selection that might have occurred from double-click
      editor.selection.collapse();
      mappingSessionStore.clearTextSelection();

      // Get the closest editable element (cell or paragraph)
      const targetElement =
        element.closest('td') ||
        element.closest('th') ||
        element.closest('p') ||
        (element as HTMLElement);

      const nodeId = targetElement?.getAttribute('data-nodeid');

      if (!nodeId || mappingSessionStore.isLocked(nodeId)) {
        return;
      }

      // Get element position relative to the viewport
      const elementRect = targetElement.getBoundingClientRect();

      // Get the editor iframe for proper positioning
      const iframe = editor.getWin().frameElement as HTMLIFrameElement;
      const iframeRect = iframe ? iframe.getBoundingClientRect() : { left: 0, top: 0 };

      // Calculate position relative to the page, accounting for iframe offset
      const absoluteRect = new DOMRect(
        elementRect.left + iframeRect.left,
        elementRect.top + iframeRect.top,
        elementRect.width,
        elementRect.height,
      );

      // Show editing overlay
      smartEditorStore.setEditingOverlay({
        nodeId,
        element: targetElement,
        rect: absoluteRect,
        content: targetElement.textContent || targetElement.innerText || '',
      });
    };

    const handleOverlaySubmit = (newContent: string) => {
      if (smartEditorStore.editingOverlay) {
        const { nodeId, element } = smartEditorStore.editingOverlay;
        const oldContent = element.textContent || element.innerText || '';

        // Update only the text content, preserving HTML structure
        if (element.children.length > 0) {
          // If element has child elements, update the text content while preserving structure
          element.textContent = newContent;
        } else {
          // If it's a simple text element, just update innerHTML
          element.innerHTML = newContent;
        }

        // Record the change for efficient tracking
        mappingSessionStore.recordNodeChange('target', nodeId, oldContent, newContent);
        if (smartEditorStore instanceof TargetSmartEditorStore) {
          smartEditorStore.refreshAuthoringHighlightForNode(nodeId);
        }

        // Close overlay
        smartEditorStore.setEditingOverlay(undefined);
      }
    };

    const handleOverlayCancel = () => {
      smartEditorStore.setEditingOverlay(undefined);
    };

    /**
     * Handle element click in editor
     * Delegates element selection to appropriate store based on editor type
     *
     * @method handleElementClick
     * @param {Element} element - DOM element that was clicked
     * @returns {void}
     *
     * @example
     * // Called when user clicks an element in editor
     * handleElementClick(domElement);
     */
    const handleElementClick = (element: Element, isMultiSelect: boolean) => {
      smartEditorStore.setIsUserInteraction(true);
      smartEditorStore.handleElementSelection(element, isMultiSelect);
      smartEditorStore.setIsUserInteraction(false);
    };

    /**
     * Get TinyMCE editor configuration
     * Builds complete editor configuration based on editor type and mode
     *
     * @method getEditorConfig
     * @param {SmartEditorType} type - Editor type (source/target)
     * @param {string} height - Editor height
     * @param {EditorMode} editorMode - Current editor mode
     * @param {string | boolean} menubar - Menubar configuration
     * @param {string} [removedMenuitems] - Menu items to remove
     * @param {string} [plugins] - Additional plugins to enable
     * @param {string} [toolbar] - Custom toolbar configuration
     * @returns {EditorConfig} Complete TinyMCE configuration object
     *
     * @example
     * const config = getEditorConfig(
     *   'source',
     *   '60vh',
     *   EditorMode.TRANSFER,
     *   'file edit view',
     * );
     */
    const getEditorConfig = (
      type: SmartEditorType,
      height: string,
      editorMode: EditorMode,
      menubar: string | boolean,
      removedMenuitems?: string,
      plugins?: string,
      toolbar?: string,
    ) => ({
      powerpaste_allow_local_images: true,
      paste_data_images: true,
      paste_as_text: false,
      powerpaste_word_import: 'merge',
      height,
      skin: 'fluent',
      icons: 'material',
      elementpath: false,
      content_css: 'fluent',
      branding: false,
      contextmenu: 'acceptValue selectValue',
      contextmenu_avoid_overlap: '.mce-spelling-word',
      menubar,
      removed_menuitems: removedMenuitems || '',
      plugins: plugins || '',
      table_toolbar: '',
      table_resize_bars: false,
      table_grid: false,
      table_tab_navigation: false,
      table_advtab: false,
      table_cell_advtab: false,
      table_row_advtab: false,
      table_appearance_options: false,
      toolbar: toolbar || '',
      visual: false,
      content_style: `
        ${DOCUMENT_STYLES}
        ${DOCUMENT_MARGINS}
        ${type !== 'source' ? MSO_DEFINITIONS : ''}
        ${
          editorMode === EditorMode.TRANSFER
            ? `
            ::selection {
              background: #ffc36a !important;
            }
            ::-moz-selection {
              background: #ffc36a !important;
            }
            ${
              type === 'source'
                ? `
            * {
              caret-color: transparent !important;
              -webkit-caret-color: transparent !important;
            }
            .mce-content-body {
              cursor: default !important;
              user-select: none !important;
              -webkit-user-select: none !important;
              outline: none !important;
            }
            .mce-content-body * {
              cursor: default !important;
              user-select: text !important;
              -webkit-user-select: text !important;
              outline: none !important;
              -webkit-touch-callout: none !important;
              -webkit-tap-highlight-color: transparent !important;
            }
            .mce-content-body p,
            .mce-content-body span,
            .mce-content-body div:not(:empty),
            .mce-content-body td {
              cursor: text !important;
            }
            [contenteditable] {
              -webkit-user-modify: read-only !important;
              -moz-user-modify: read-only !important;
              user-modify: read-only !important;
            }
            .tox-edit-area__iframe {
              pointer-events: none !important;
            }
            .tox-edit-area__iframe * {
              pointer-events: auto !important;
            }
            /* Hide page numbers */
            .mce-content-body::after,
            .mce-content-body::before {
              display: none !important;
            }
            /* Hide any elements that might show page numbers */
            [data-page-number],
            .page-number,
            .page-count {
              display: none !important;
            }
            `
                : ''
            }
          `
            : ''
        }
        [data-highlight="partial"] { background-color: #ffc36a !important; }
      `,
      deprecation_warnings: false,
      spellchecker_language: 'en',
      typography_default_lang: 'en-US',
      table_use_colgroups: false,
      style_formats_merge: false,
      importcss_append: true,
      extended_valid_elements: `
        *[data-highlight|data-nodeid|data-mce-style|data-sourceid|data-selectionid|data-docid|data-nodeorder|data-newnode],
        span[data-highlight|style|data-mce-style|data-sourceid|data-selectionid|data-docid|data-nodeorder|data-newnode]
      `,
      setup: (editor: TinyMCEEditor) => {
        // Register basic formats if not already registered
        editor.on('init', () => {
          try {
            editor.formatter.register('bold', { inline: 'b', toggle: true, remove: 'all' });
            editor.formatter.register('italic', { inline: 'i', toggle: true, remove: 'all' });
            editor.formatter.register('underline', { inline: 'u', toggle: true, remove: 'all' });
            editor.formatter.register('strikethrough', {
              inline: 'span',
              toggle: true,
              styles: { 'text-decoration': 'line-through' },
              remove: 'all',
            });
          } catch (e) {
            // Formats already registered by TinyMCE
          }

          editor.formatter.register('highlight', {
            inline: 'span',
            attributes: {
              'data-highlight': 'partial',
            },
            styles: {
              'background-color': '%value',
            },
          });
        });
      },
      custom_elements:
        '~span[data-highlight|data-nodeid|data-sourceid|data-selectionid|data-docid|data-nodeorder|data-newnode]',
    });

    /**
     * Memoized editor configuration
     * Caches editor config to prevent unnecessary recalculations
     *
     * @property {EditorConfig} editorConfig
     * @dependency {SmartEditorType} type - Editor type
     * @dependency {string} height - Editor height
     * @dependency {EditorMode} mappingSessionStore.editorMode - Editor mode
     * @dependency {string | boolean} editorMenubar - Menubar config
     * @dependency {string} editorRemovedMenuitems - Removed menu items
     * @dependency {string} editorPlugins - Editor plugins
     * @dependency {string} editorToolbar - Toolbar config
     *
     * @example
     * // Used in Editor component
     * <Editor init={editorConfig} />
     */
    const editorConfig = useMemo(
      () =>
        getEditorConfig(
          type,
          height,
          mappingSessionStore.editorMode,
          editorMenubar,
          editorRemovedMenuitems,
          editorPlugins,
          editorToolbar,
        ),
      [
        type,
        height,
        mappingSessionStore.editorMode,
        editorMenubar,
        editorRemovedMenuitems,
        editorPlugins,
        editorToolbar,
      ],
    );

    return (
      <SmartEditorProvider store={smartEditorStoreRef}>
        <Observer>
          {() => (
            <div className="flex flex-col h-full overflow-hidden min-h-0">
              <Editor
                apiKey={REACT_APP_TINYMCE_API_KEY}
                onInit={handleEditorInit}
                init={editorConfig}
              />
              <Slider
                value={smartEditorStore.fontSize}
                changeHandler={handleFontSizeChange}
                min={80}
                max={130}
                step={2}
              />

              {/* Editing Overlay */}
              {smartEditorStore.editingOverlay && (
                <EditingOverlay
                  nodeId={smartEditorStore.editingOverlay.nodeId}
                  content={smartEditorStore.editingOverlay.content}
                  rect={smartEditorStore.editingOverlay.rect}
                  onSubmit={handleOverlaySubmit}
                  onCancel={handleOverlayCancel}
                />
              )}
            </div>
          )}
        </Observer>
      </SmartEditorProvider>
    );
  },
);

SmartEditor.displayName = 'SmartEditor';

/**
 * Editing Overlay Component
 * Provides a contenteditable overlay for cell editing
 */
interface EditingOverlayProps {
  nodeId: string;
  content: string;
  rect: DOMRect;
  onSubmit: (content: string) => void;
  onCancel: () => void;
}

const EditingOverlay = ({
  nodeId: _nodeId,
  content,
  rect,
  onSubmit,
  onCancel,
}: EditingOverlayProps) => {
  const [editContent, setEditContent] = useState(content);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Focus and select content when overlay opens
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      onSubmit(editContent);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  const handleSubmit = () => {
    onSubmit(editContent);
  };

  return (
    <div
      className="fixed z-50 bg-white border-2 border-blue-500 rounded shadow-lg"
      style={{
        left:
          rect.left + rect.width + 10 < window.innerWidth - 320
            ? rect.left + rect.width + 10 // Position to the right if there's space
            : Math.max(10, rect.left - 330), // Otherwise position to the left
        top: Math.max(10, Math.min(rect.top, window.innerHeight - 250)), // Keep within viewport
        width: 320,
        maxHeight: 240,
      }}>
      <div className="p-2">
        <textarea
          ref={textareaRef}
          value={editContent}
          onChange={(e) => setEditContent(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full h-full min-h-[80px] p-2 border border-gray-300 rounded resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
          style={{
            fontSize: '14px',
            lineHeight: '1.4',
            fontFamily: 'inherit',
          }}
        />
        <div className="flex gap-2 mt-2">
          <button
            onClick={handleSubmit}
            className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600">
            Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
